/**
 * Pure helpers over the tenant's API access (`GET dataset-access`) and keys.
 */

/** Canonical read-grant key for a dataset — the scope an API key carries. */
export const datasetScopeKey = (datasetId) => `dataset.${datasetId}.read`;

/** `dataset.<id>.read` → `<id>`; null for any other scope. */
export function datasetIdFromScope(scope) {
  const parts = String(scope || '').split('.');
  return parts.length === 3 && parts[0] === 'dataset' && parts[2] === 'read' ? parts[1] : null;
}

/** True when the allowed keys include at least one `dataset.<id>.api` grant. */
export function hasDatasetApiGrant(allowed) {
  return (allowed || []).some((key) => /^dataset\.[^.*]+\.api$/.test(String(key)));
}

/**
 * Everything the tenant can call today: single datasets plus every member of
 * an owned bundle (Scrumble does not repeat bundle members in `datasets`).
 * Stopping items still count until their access ends.
 */
export function ownedSets(access) {
  const bundles = access?.bundles || [];
  const datasetIds = new Set((access?.datasets || []).map((row) => row.dataset_id).filter(Boolean));
  bundles.forEach((bundle) => (bundle.dataset_ids || []).forEach((id) => datasetIds.add(id)));
  return {
    datasetIds: [...datasetIds],
    bundleSlugs: bundles.map((bundle) => bundle.slug).filter(Boolean),
  };
}

/** What the active (billed) items cost per month, in cents. Stopping items are excluded. */
export function activeMonthlyTotal(access) {
  const rows = [...(access?.datasets || []), ...(access?.bundles || [])];
  return rows
    .filter((row) => row.status === 'active')
    .reduce((sum, row) => sum + (Number.isFinite(row.amount_month) ? row.amount_month : 0), 0);
}

/** Keys that still work: not revoked and not expired. */
export function activeKeys(keys, now = new Date()) {
  return (keys || []).filter(
    (key) => !key.revoked_at && !(key.expires_at && new Date(key.expires_at) <= now)
  );
}

/**
 * Active keys that would lose ALL their data access when `datasetIds` stop:
 * every dataset scope they carry is in the stopped set. Keys with
 * `all_entitlements` or a scope outside the set keep working.
 */
export function keysOnlyScopedTo(keys, datasetIds, now = new Date()) {
  const stopping = new Set(datasetIds || []);
  return activeKeys(keys, now).filter((key) => {
    if (key.all_entitlements) return false;
    const datasetScopes = (key.scopes || []).map(datasetIdFromScope).filter(Boolean);
    return datasetScopes.length > 0 && datasetScopes.every((id) => stopping.has(id));
  });
}
